import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Calendar, Clock, MapPin, Lightning, ShieldCheck, Warning } from "@phosphor-icons/react";

const HOURS_LIST = Array.from({ length: 11 }, (_, i) => String(7 + i).padStart(2, "0") + ":00");
const DIFF_H = (a, b) => {
    const [ah, am] = a.split(":").map(Number);
    const [bh, bm] = b.split(":").map(Number);
    return (bh * 60 + bm - ah * 60 - am) / 60;
};

export default function Agenda() {
    const { user } = useAuth();
    const [slots, setSlots] = useState([]);
    const [clients, setClients] = useState([]);
    const [techs, setTechs] = useState([]);
    const [especialidades, setEspecialidades] = useState([]);
    const [sedes, setSedes] = useState([]);
    const [evalStatus, setEvalStatus] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const isSupervisor = user?.role === "supervisor";
    const isComercial = user?.role === "comercial";
    const canCreate = isSupervisor || isComercial;

    const blankForm = () => ({
        kind: isComercial ? "evaluacion_inicial" : "tecnica",
        fecha: new Date().toISOString().slice(0, 10),
        hora_inicio: "08:00", hora_fin: "12:00",
        client_id: "", sede_id: "",
        actividad: isComercial ? "Evaluación inicial de planta física y definición de sistemas a mantener" : "",
        recomendaciones_supervisor: "",
        norma_iso: "ISO 9001:2015",
        especialidad_requerida: "",
        technician_cedula: "",
    });
    const [form, setForm] = useState(blankForm());

    const load = async () => setSlots((await api.get("/agenda")).data);

    useEffect(() => {
        load();
        (async () => {
            const [c, e] = await Promise.all([api.get("/clients"), api.get("/especialidades")]);
            setClients(c.data); setEspecialidades(e.data);
        })();
    }, []);

    // Cargar técnicos según especialidad (solo supervisor)
    useEffect(() => {
        if (!isSupervisor) return;
        if (!form.especialidad_requerida) { setTechs([]); return; }
        (async () => {
            const r = await api.get(`/technicians?especialidad=${encodeURIComponent(form.especialidad_requerida)}&activo=true`);
            setTechs(r.data);
        })();
    }, [form.especialidad_requerida, isSupervisor]);

    const loadSedesAndEval = async (cid) => {
        if (!cid) { setSedes([]); setEvalStatus(null); return; }
        const [s, ev] = await Promise.all([
            api.get(`/clients/${cid}/sedes`),
            api.get(`/clients/${cid}/evaluation-status`),
        ]);
        setSedes(s.data);
        setEvalStatus(ev.data);
    };

    const onClient = (cid) => {
        setForm(f => ({ ...f, client_id: cid, sede_id: "" }));
        loadSedesAndEval(cid);
    };

    const submit = async (e) => {
        e.preventDefault(); setError(""); setSubmitting(true);
        // Validación cliente: min 4h
        const dur = DIFF_H(form.hora_inicio, form.hora_fin);
        if (dur < 4) {
            setError(`La visita debe ser de mínimo medio día (4 horas). Actualmente: ${dur}h`);
            setSubmitting(false); return;
        }
        try {
            const payload = { ...form };
            if (form.kind === "evaluacion_inicial") {
                delete payload.technician_cedula;
                delete payload.especialidad_requerida;
            }
            await api.post("/agenda", payload);
            setShowForm(false);
            setForm(blankForm());
            setEvalStatus(null); setSedes([]);
            load();
        } catch (err) {
            setError(err.response?.data?.detail || "Error al agendar");
        }
        setSubmitting(false);
    };

    const byDate = slots.reduce((acc, s) => { (acc[s.fecha] = acc[s.fecha] || []).push(s); return acc; }, {});
    const evalCompletada = evalStatus?.completada;
    const canSubmitTecnica = !isSupervisor || form.kind !== "tecnica" || !form.client_id || evalCompletada;

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-8" data-testid="agenda-page">
            <div className="flex items-end justify-between mb-8 border-b border-zinc-200 pb-6">
                <div>
                    <div className="overline mb-2" style={{ color: "#0F766E" }}>Calendario operacional</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter">Agenda</h1>
                    <p className="text-sm text-zinc-600 mt-2">
                        {isComercial && "Agenda la visita inicial del Supervisor para la evaluación de planta física. Mínimo medio día (4h)."}
                        {isSupervisor && "Programa visitas técnicas según el plan adquirido. Requiere evaluación inicial cerrada. Mínimo medio día (4h)."}
                        {!canCreate && "Tu programación de visitas asignada."}
                    </p>
                </div>
                {canCreate && (
                    <button onClick={() => setShowForm(s => !s)} className="btn-accent flex items-center gap-2" data-testid="toggle-agenda-new">
                        <Plus size={16} weight="bold" /> {showForm ? "Cancelar" : (isComercial ? "Agendar evaluación inicial" : "Agendar visita técnica")}
                    </button>
                )}
            </div>

            {showForm && canCreate && (
                <form onSubmit={submit} className="border border-zinc-200 p-6 bg-zinc-50 mb-8 grid md:grid-cols-2 gap-4" data-testid="agenda-form">
                    {isComercial && (
                        <div className="md:col-span-2 p-3 border border-teal-200 bg-teal-50 text-xs text-teal-900 flex items-start gap-2">
                            <ShieldCheck size={16} weight="fill" />
                            <div>
                                <strong>Evaluación inicial obligatoria.</strong> El sistema auto-asignará al Supervisor con menor carga ese día y validará que no haya cruce de horarios. Esta visita debe completarse y cerrarse con clasificación de sistemas antes de poder agendar técnicos.
                            </div>
                        </div>
                    )}

                    <label><div className="overline mb-1.5">Cliente *</div>
                        <select className="input-atia" value={form.client_id} onChange={e => onClient(e.target.value)} required data-testid="ag-client">
                            <option value="">— Selecciona —</option>
                            {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.razon_social} · NIT {c.nit}</option>)}
                        </select>
                    </label>
                    <label><div className="overline mb-1.5">Sede</div>
                        <select className="input-atia" value={form.sede_id} onChange={e => setForm({...form, sede_id: e.target.value})} data-testid="ag-sede" disabled={!sedes.length}>
                            <option value="">{sedes.length ? "— Selecciona sede —" : "Selecciona cliente primero"}</option>
                            {sedes.map(s => <option key={s.sede_id} value={s.sede_id}>{s.nombre} — {s.direccion}</option>)}
                        </select>
                    </label>

                    {/* Estado de evaluación inicial — visible cuando hay cliente */}
                    {evalStatus && (
                        <div className="md:col-span-2" data-testid="eval-status">
                            {evalStatus.completada && (
                                <div className="p-3 border border-emerald-200 bg-emerald-50 text-xs text-emerald-900 flex items-start gap-2">
                                    <ShieldCheck size={16} weight="fill" />
                                    <div>
                                        <strong>Evaluación inicial completada.</strong> {evalStatus.sistemas_evaluados.length} sistemas evaluados. Puedes agendar técnicos según especialidad.
                                    </div>
                                </div>
                            )}
                            {evalStatus.pendiente && (
                                <div className="p-3 border border-amber-300 bg-amber-50 text-xs text-amber-900 flex items-start gap-2">
                                    <Warning size={16} weight="fill" />
                                    <div>
                                        <strong>Evaluación inicial pendiente.</strong> Orden {evalStatus.pendiente_order_number} aún sin cerrar. {isSupervisor && "Debes cerrarla antes de agendar técnicos."}
                                    </div>
                                </div>
                            )}
                            {!evalStatus.completada && !evalStatus.pendiente && (
                                <div className="p-3 border border-zinc-300 bg-white text-xs text-zinc-700 flex items-start gap-2">
                                    <Warning size={16} />
                                    <div>
                                        <strong>Sin evaluación inicial.</strong> {isComercial ? "Procede a agendarla." : "Pide al área comercial que la agende primero."}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Especialidad + técnico (solo supervisor + kind tecnica) */}
                    {isSupervisor && (
                        <>
                            <label><div className="overline mb-1.5">Especialidad requerida *</div>
                                <select className="input-atia" value={form.especialidad_requerida} onChange={e => setForm({...form, especialidad_requerida: e.target.value, technician_cedula: ""})} required data-testid="ag-especialidad">
                                    <option value="">— Selecciona especialidad —</option>
                                    {especialidades.map(e => <option key={e} value={e}>{e}</option>)}
                                </select>
                            </label>
                            <label><div className="overline mb-1.5">Técnico ({form.especialidad_requerida || "selecciona especialidad"}) *</div>
                                <select className="input-atia" value={form.technician_cedula} onChange={e => setForm({...form, technician_cedula: e.target.value})} required disabled={!form.especialidad_requerida} data-testid="ag-tech">
                                    <option value="">{form.especialidad_requerida ? (techs.length ? "— Selecciona técnico —" : "Sin técnicos activos en esta especialidad") : "Primero elige especialidad"}</option>
                                    {techs.map(t => <option key={t.tech_id} value={t.cedula}>{t.nombre} · CC {t.cedula}</option>)}
                                </select>
                            </label>
                        </>
                    )}

                    <label><div className="overline mb-1.5">Norma aplicable *</div>
                        <select className="input-atia" value={form.norma_iso} onChange={e => setForm({...form, norma_iso: e.target.value})} data-testid="ag-norma">
                            {["ISO 9001:2015", "ISO 17020:2012", "ISO 45001:2018", "ISO 14001:2015"].map(n => <option key={n}>{n}</option>)}
                        </select>
                    </label>
                    <label><div className="overline mb-1.5">Fecha *</div>
                        <input type="date" className="input-atia" required value={form.fecha} onChange={e => setForm({...form, fecha: e.target.value})} data-testid="ag-fecha" />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <label><div className="overline mb-1.5">Hora inicio *</div>
                            <select className="input-atia font-mono" value={form.hora_inicio} onChange={e => setForm({...form, hora_inicio: e.target.value})} required data-testid="ag-ini">
                                {HOURS_LIST.map(h => <option key={h}>{h}</option>)}
                            </select>
                        </label>
                        <label><div className="overline mb-1.5">Hora fin * <span className="text-zinc-400 normal-case text-[10px] font-normal">(mín. 4h)</span></div>
                            <select className="input-atia font-mono" value={form.hora_fin} onChange={e => setForm({...form, hora_fin: e.target.value})} required data-testid="ag-fin">
                                {HOURS_LIST.map(h => <option key={h}>{h}</option>)}
                            </select>
                        </label>
                    </div>
                    <label className="md:col-span-2"><div className="overline mb-1.5">Actividad a realizar *</div>
                        <input className="input-atia" required value={form.actividad} onChange={e => setForm({...form, actividad: e.target.value})} data-testid="ag-actividad" placeholder={isComercial ? "Evaluación inicial de planta física" : "Ej: Inspección HVAC trimestral · revisión bobinas y filtros"} />
                    </label>
                    {isSupervisor && (
                        <label className="md:col-span-2"><div className="overline mb-1.5">Recomendaciones del supervisor al técnico</div>
                            <textarea rows={3} className="input-atia" value={form.recomendaciones_supervisor} onChange={e => setForm({...form, recomendaciones_supervisor: e.target.value})} data-testid="ag-reco" placeholder="Ej: Revisar registros previos, llevar termógrafo y multímetro calibrado…" />
                        </label>
                    )}
                    {error && <div className="md:col-span-2 p-3 border border-red-300 bg-red-50 text-red-900 text-sm" data-testid="ag-error">{error}</div>}
                    <div className="md:col-span-2 flex justify-end">
                        <button type="submit" disabled={submitting || !canSubmitTecnica} className="btn-primary" data-testid="submit-agenda">
                            {submitting ? "Guardando…" : (isComercial ? "Agendar evaluación inicial →" : "Agendar y crear orden →")}
                        </button>
                    </div>
                </form>
            )}

            <div className="space-y-6">
                {Object.keys(byDate).length === 0 && <div className="p-12 text-center text-zinc-500 border border-dashed border-zinc-300">Sin visitas agendadas.</div>}
                {Object.keys(byDate).sort().map(fecha => (
                    <div key={fecha} data-testid={`agenda-day-${fecha}`}>
                        <div className="overline mb-3 flex items-center gap-2"><Calendar size={12} /> {fecha}</div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {byDate[fecha].map(s => {
                                const isEval = s.kind === "evaluacion_inicial";
                                return (
                                    <div key={s.slot_id} className="border border-zinc-200 p-4 relative" style={{ borderLeft: isEval ? "4px solid #0F766E" : "4px solid #14B8A6" }} data-testid={`slot-${s.slot_id}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-mono text-sm font-bold flex items-center gap-1"><Clock size={12} /> {s.hora_inicio} — {s.hora_fin}</span>
                                            <span className="badge-sev sev-media">{s.estado}</span>
                                        </div>
                                        {isEval && <div className="overline mb-1" style={{ color: "#0F766E" }}>Evaluación inicial (Supervisor)</div>}
                                        <div className="font-display text-lg font-bold tracking-tight leading-tight">{s.client?.razon_social || "—"}</div>
                                        <div className="text-xs text-zinc-600 mt-1">{s.actividad}</div>
                                        {s.sede && <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1"><MapPin size={11} /> {s.sede.nombre} · {s.sede.direccion}</div>}
                                        <div className="text-[11px] text-zinc-600 mt-2">
                                            {isEval ? "🛡️" : "👤"} {s.assignee?.nombre || s.technician?.nombre || s.assignee_cedula || s.technician_cedula}
                                            {s.assignee?.especialidad && <span className="ml-2 text-zinc-500">· {s.assignee.especialidad}</span>}
                                            {s.especialidad_requerida && !s.assignee?.especialidad && <span className="ml-2 text-zinc-500">· {s.especialidad_requerida}</span>}
                                        </div>
                                        {s.recomendaciones_supervisor && (
                                            <div className="mt-3 pt-3 border-t border-zinc-100">
                                                <div className="overline text-[9px]" style={{ color: "#0F766E" }}>Indicaciones del supervisor</div>
                                                <p className="text-xs text-zinc-700 mt-1">{s.recomendaciones_supervisor}</p>
                                            </div>
                                        )}
                                        {s.order_id && (
                                            <a href={`/orders/${s.order_id}`} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "#0F766E" }}>
                                                <Lightning size={12} weight="fill" /> Ver orden →
                                            </a>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
